import type { DetailedRisk } from '../types/siteRag';
import type { FindingClaim } from './findingClaim';
import { formatFindingClaimDisplay, normalizeFindingClaim } from './findingClaim';

function inferLikelihood(text: string): DetailedRisk['riskLevel'] {
  const lower = text.toLowerCase();
  if (/\b(high|critical|severe|major|significant|substantial)\b/.test(lower)) return 'High';
  if (/\b(medium|moderate|material|elevated)\b/.test(lower)) return 'Medium';
  if (/\b(low|minor|limited|negligible|minimal)\b/.test(lower)) return 'Low';
  return 'Medium';
}

function inferImpact(
  text: string,
  likelihood: DetailedRisk['riskLevel'],
): DetailedRisk['impactLevel'] {
  const lower = text.toLowerCase();
  if (/\b(critical|catastrophic|blocking|infeasible|impossible)\b/.test(lower)) return 'Red';
  if (/\b(high impact|severe|major|significant)\b/.test(lower)) return 'Red';
  if (/\b(medium impact|moderate|material)\b/.test(lower)) return 'Amber';
  if (likelihood === 'High') return 'Red';
  if (likelihood === 'Medium') return 'Amber';
  return 'Green';
}

/** Split "Category: description" power/env risk strings into DetailedRisk rows. */
export function parseStringRiskEntry(raw: string): DetailedRisk {
  const trimmed = raw.trim();
  const colonIdx = trimmed.indexOf(':');
  let category = 'Risk';
  let reason = trimmed;

  if (colonIdx > 0 && colonIdx < 96) {
    category = trimmed.slice(0, colonIdx).trim();
    reason = trimmed.slice(colonIdx + 1).trim();
  }

  const riskLevel = inferLikelihood(`${category} ${reason}`);
  const impactLevel = inferImpact(reason, riskLevel);

  return {
    category,
    reason,
    riskLevel,
    impactLevel,
    action: '',
  };
}

export function parseStringRiskEntries(entries: string[]): DetailedRisk[] {
  return entries
    .map(e => e.trim())
    .filter(Boolean)
    .map(parseStringRiskEntry);
}

type RiskSourceEntry = string | FindingClaim;

/** Prefer structured risks; otherwise parse string / FindingClaim lists. Dedupes by category+reason. */
export function coalesceReportRisks(
  structured: DetailedRisk[] | undefined,
  stringSources: (RiskSourceEntry[] | undefined)[],
): DetailedRisk[] {
  if (structured?.length) return structured;

  const seen = new Set<string>();
  const out: DetailedRisk[] = [];
  for (const group of stringSources) {
    for (const raw of group ?? []) {
      const normalized =
        typeof raw === 'string' ? raw : normalizeFindingClaim(raw);
      const t = typeof raw === 'string' ? raw.trim() : normalized ? formatFindingClaimDisplay(normalized) : '';
      if (!t || seen.has(t)) continue;
      seen.add(t);
      out.push(parseStringRiskEntry(t));
    }
  }
  return out;
}
